// MediaPipe 识别：人脸关键点 + 多类别分割（背景/头发/身体皮肤/脸部皮肤/衣服/其它）
// 模型和 wasm 都放在自己的域名下，国内也能加载；首次约 30MB，之后由 Service Worker 缓存。

import { FaceLandmarker, FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision";
import type { Pt } from "./geometry";

const ASSET_VERSION = "1";
const WASM_BASE = "/wasm";
const FILES = {
  wasmJs: `${WASM_BASE}/vision_wasm_internal.js`,
  wasm: `${WASM_BASE}/vision_wasm_internal.wasm`,
  face: `/models/face_landmarker.task?v=${ASSET_VERSION}`,
  seg: `/models/selfie_multiclass_256x256.tflite?v=${ASSET_VERSION}`,
};
/** 用于进度条的预估体积（字节） */
const SIZES: Record<keyof typeof FILES, number> = {
  wasmJs: 323_377,
  wasm: 11_756_954,
  face: 3_758_596,
  seg: 16_371_837,
};

/** 分割模型输出的类别顺序 */
export const SEG = { background: 0, hair: 1, bodySkin: 2, faceSkin: 3, clothes: 4, others: 5 } as const;

export interface MaskSet {
  w: number;
  h: number;
  hair: Uint8Array;
  faceSkin: Uint8Array;
  bodySkin: Uint8Array;
}

export interface Analysis {
  /** 原图像素坐标下的 478 个关键点；没识别到脸为 null */
  landmarks: Pt[] | null;
  masks: MaskSet;
}

interface Engine {
  face: FaceLandmarker;
  seg: ImageSegmenter;
}

let enginePromise: Promise<Engine> | null = null;

export type LoadProgress = (loaded: number, total: number) => void;

export function loadVision(onProgress?: LoadProgress): Promise<Engine> {
  if (!enginePromise) {
    enginePromise = createEngine(onProgress).catch((err) => {
      enginePromise = null;
      throw err;
    });
  }
  return enginePromise;
}

export function visionReady(): boolean {
  return engineCache !== null;
}

let engineCache: Engine | null = null;

async function createEngine(onProgress?: LoadProgress): Promise<Engine> {
  const total = Object.values(SIZES).reduce((a, b) => a + b, 0);
  const loaded: Record<string, number> = {};
  const report = () =>
    onProgress?.(
      Object.values(loaded).reduce((a, b) => a + b, 0),
      total,
    );

  // 老设备不支持 SIMD 时库会换成 nosimd 版本，按它选的路径预取
  const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
  const urls: Record<keyof typeof FILES, string> = {
    wasmJs: fileset.wasmLoaderPath,
    wasm: fileset.wasmBinaryPath,
    face: FILES.face,
    seg: FILES.seg,
  };

  // 四个文件并行拉取并汇报进度；wasm 预热进缓存，库再取时直接命中
  const [, , faceBuf, segBuf] = await Promise.all(
    (Object.keys(urls) as (keyof typeof FILES)[]).map((k) =>
      fetchWithProgress(urls[k], (n) => {
        loaded[k] = n;
        report();
      }),
    ),
  );

  // iOS Safari 上 GPU 分割会输出错误类别，统一走 CPU
  const face = await FaceLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetBuffer: new Uint8Array(faceBuf), delegate: "CPU" },
    runningMode: "IMAGE",
    numFaces: 1,
    minFaceDetectionConfidence: 0.4,
    minFacePresenceConfidence: 0.4,
  });
  const seg = await ImageSegmenter.createFromOptions(fileset, {
    baseOptions: { modelAssetBuffer: new Uint8Array(segBuf), delegate: "CPU" },
    runningMode: "IMAGE",
    outputConfidenceMasks: true,
    outputCategoryMask: false,
  });
  engineCache = { face, seg };
  return engineCache;
}

async function fetchWithProgress(url: string, onBytes: (n: number) => void): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok || !res.body) throw new Error(`加载失败：${url}（${res.status}）`);
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let n = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    n += value.length;
    onBytes(n);
  }
  const out = new Uint8Array(n);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out.buffer;
}

/** 分析一张图。分割在 ≤512px 的缩略图上跑，关键点换算回原图坐标 */
export async function analyze(source: HTMLCanvasElement | ImageBitmap): Promise<Analysis> {
  const engine = await loadVision();
  const W = source.width;
  const H = source.height;

  const small = scaleCanvas(source, 512);
  const mid = scaleCanvas(source, 1024);
  const faceRes = engine.face.detect(mid);
  const lm = faceRes.faceLandmarks?.[0];
  const landmarks: Pt[] | null = lm ? lm.map((p) => [p.x * W, p.y * H] as Pt) : null;

  const segRes = engine.seg.segment(small);
  const conf = segRes.confidenceMasks;
  if (!conf || conf.length < 4) throw new Error("分割模型输出异常");
  const mw = conf[0].width;
  const mh = conf[0].height;
  const toU8 = (i: number) => {
    const f = conf[i].getAsFloat32Array();
    const out = new Uint8Array(f.length);
    for (let k = 0; k < f.length; k++) out[k] = f[k] * 255 + 0.5;
    return out;
  };
  const masks: MaskSet = {
    w: mw,
    h: mh,
    hair: toU8(SEG.hair),
    faceSkin: toU8(SEG.faceSkin),
    bodySkin: toU8(SEG.bodySkin),
  };
  segRes.close();
  return { landmarks, masks };
}

export function scaleCanvas(src: CanvasImageSource & { width: number; height: number }, maxSide: number) {
  const s = Math.min(1, maxSide / Math.max(src.width, src.height));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(src.width * s));
  c.height = Math.max(1, Math.round(src.height * s));
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

// ---------- 关键点分组（MediaPipe Face Mesh 索引） ----------

export const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136,
  172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
];
export const LEFT_EYE = [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466];
export const RIGHT_EYE = [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246];
export const LEFT_BROW = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];
export const RIGHT_BROW = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
export const LIPS = [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185];
export const NOSTRILS = [48, 64, 98, 97, 2, 326, 327, 294, 278, 219, 218, 79, 309, 438, 439];
/** 下眼睑（从外眼角到内眼角），用于淡化黑眼圈 */
export const LEFT_LOWER_LID = [263, 249, 390, 373, 374, 380, 381, 382, 362];
export const RIGHT_LOWER_LID = [33, 7, 163, 144, 145, 153, 154, 155, 133];
/** 对齐用的稳定点：眼角、鼻梁、鼻尖、嘴角、下巴、脸颊轮廓 */
export const STABLE_POINTS = [33, 133, 362, 263, 168, 6, 4, 1, 61, 291, 199, 152, 234, 454, 93, 323, 58, 288, 10];

export function pick(landmarks: Pt[], idx: number[]): Pt[] {
  return idx.map((i) => landmarks[i]);
}

/** 脸部外接框（原图坐标） */
export function faceBox(landmarks: Pt[]) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const i of FACE_OVAL) {
    const [x, y] = landmarks[i];
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { cx: (minX + maxX) / 2, top: minY, width: maxX - minX, height: maxY - minY };
}

/** 粗估左右转头角度：鼻尖相对两眼外角的位置，0 = 正脸，±1 = 全侧 */
export function estimateYaw(landmarks: Pt[]): number {
  const l = landmarks[33][0];
  const r = landmarks[263][0];
  const nose = landmarks[1][0];
  const mid = (l + r) / 2;
  const half = Math.abs(r - l) / 2 || 1;
  return Math.max(-1, Math.min(1, (nose - mid) / half));
}
